/**
 * Where each file of a batch stands, as the uploader sees it: on the platform, on its way, ready, or
 * held back, and why (ANON-05, UPL-06, UPL-10). Pure functions over the pipeline's items, so the
 * progress bar, the counts and the problem list always agree.
 */
import type { MessageKey } from '../lib/i18n'
import type { Item, ItemStatus } from './pipeline'
import { explanations, remedy } from './reasons'

export type Outcome =
  | 'uploaded' // accepted by the platform
  | 'progress' // being checked, anonymized, sent or validated
  | 'ready' // checked, not sent yet
  | 'unchecked' // chosen, not checked yet
  | 'needs' // blocked by a problem the uploader can correct here
  | 'failed' // blocked, failed or rejected for a reason to look into
  | 'leftout' // not for the platform: not DICOM, not a radiograph, or left out by the uploader
  | 'already' // on the platform already, so neither anonymized nor stored again

/** In the order the progress bar shows them, from done to held back. */
export const OUTCOMES: readonly Outcome[] = ['uploaded', 'progress', 'ready', 'unchecked', 'needs', 'failed', 'leftout', 'already']
/** The outcomes that keep files from the platform, each with its reasons. */
export const HELD_BACK: readonly Outcome[] = ['needs', 'failed', 'leftout', 'already']
/** A list of an upload's files shows at most this many rows; a line counts the rest. */
export const MAX_ROWS = 200

const ON_THE_WAY: ReadonlySet<ItemStatus> = new Set(['processing', 'anonymized', 'transferring', 'pending', 'uploaded', 'validating'])
/** Reached the platform: sent and answered, or known there before anything was sent. */
const SENT: ReadonlySet<ItemStatus> = new Set(['uploaded', 'validating', 'accepted', 'rejected'])
const ANSWERED: ReadonlySet<ItemStatus> = new Set(['accepted', 'rejected'])

/** Sent by this browser before, so not even read again (UPL-04). */
const sentBefore = (item: Item) => item.status === 'duplicate' && item.reason === 'sent_before'

export function outcomeOf(item: Item): Outcome {
  switch (item.status) {
    case 'accepted':
      return 'uploaded'
    case 'duplicate':
      return 'already'
    case 'rejected':
    case 'failed':
      return 'failed'
    case 'waiting':
      return item.analysis ? 'ready' : 'unchecked'
    case 'blocked': {
      const can = remedy(item.reason)
      return can === 'correct' ? 'needs' : can === 'none' ? 'leftout' : 'failed'
    }
    default:
      return ON_THE_WAY.has(item.status) ? 'progress' : 'failed'
  }
}

export interface Tally {
  total: number
  counts: Readonly<Record<Outcome, number>>
  patients: number
  studies: number
  /** Anonymized before, by the platform or the standalone app (ANON-08). */
  recognized: number
  /** Checked without a blocking problem: what an upload sends, or sent. */
  planned: number
  /** The upload's stages: anonymized, sent and answered by the platform's check (UPL-05). */
  anonymized: number
  sent: number
  answered: number
  /** Anonymized and waiting for a transfer: the network sets the pace. */
  waitingToSend: number
}

export function tally(items: readonly Item[]): Tally {
  const counts = Object.fromEntries(OUTCOMES.map((outcome) => [outcome, 0])) as Record<Outcome, number>
  const patients = new Set<string>()
  const studies = new Set<string>()
  let recognized = 0
  let planned = 0
  let anonymized = 0
  let sent = 0
  let answered = 0
  let waitingToSend = 0
  for (const item of items) {
    counts[outcomeOf(item)]++
    if (item.analysis?.patient) patients.add(item.analysis.patient)
    if (item.analysis?.study) studies.add(item.analysis.study)
    if (item.analysis?.recognized) recognized++
    const known = item.status === 'duplicate' && !sentBefore(item) // the platform knew it when it was offered
    if (item.analysis && item.status !== 'blocked' && !sentBefore(item)) planned++
    if (SENT.has(item.status) || known) sent++
    if (SENT.has(item.status) || known || item.status === 'anonymized' || item.status === 'transferring') anonymized++
    if (ANSWERED.has(item.status) || known) answered++
    if (item.status === 'anonymized') waitingToSend++
  }
  return {
    total: items.length,
    counts,
    patients: patients.size,
    studies: studies.size,
    recognized,
    planned,
    anonymized,
    sent,
    answered,
    waitingToSend,
  }
}

export interface Reason {
  key: MessageKey
  /** The codes behind it, for support (UPL-06). */
  codes: readonly string[]
  items: readonly Item[]
}

/** The files held back for one outcome, grouped by explanation; a file with several problems is under each. */
export function reasonsFor(items: readonly Item[], outcome: Outcome): Reason[] {
  const byKey = new Map<MessageKey, { codes: Set<string>; items: Item[] }>()
  for (const item of items) {
    if (outcomeOf(item) !== outcome) continue
    const found =
      item.status === 'duplicate' ? explanations(item.reason === 'sent_before' ? 'sent_before' : 'duplicate') : explanations(item.reason ?? 'unknown')
    for (const [key, codes] of found) {
      const entry = byKey.get(key) ?? { codes: new Set<string>(), items: [] }
      codes.forEach((code) => entry.codes.add(code))
      entry.items.push(item)
      byKey.set(key, entry)
    }
  }
  return [...byKey].map(([key, { codes, items: held }]) => ({ key, codes: [...codes], items: held })).sort((a, b) => b.items.length - a.items.length)
}

/** The name a file is shown by: its path in the chosen folder or archive. */
export const fileName = (item: Item) => item.file.webkitRelativePath || item.file.name
